import {computed, effect, inject, Injectable, Injector, Signal, signal, WritableSignal} from "@angular/core";
import {firstValueFrom, Subject} from "rxjs";
import {onEach} from '../utils/observable-utils';
import {SessionModel, SessionReset} from "../session/session.model";
import {AnalyticsService} from '../logging/analytics.service';
import {AppSettingData} from "../configuration/app-entity.model";
import {NavigationService} from "../navigation/navigation.service";
import {StoreData, StoreSearchModel, TableData} from '../store/store-search.model';
import {AuthService, EntityData, FedeltaImageData, SearchResult} from "@fedelta-pos/data-client";
import {FilterProvider} from "../configuration/filter.provider";
import {ConfigurationModel, ParamConfig} from "../configuration/configuration.model";
import {DeliveryDetails, OrderingContext, OrderingContextIntention, TableSelection} from './ordering-context';
import {formatDateForDisplay, formatTimeForDisplay} from '../utils/date-time-utils';
import {MessageDetails, MessageLevel} from "../notification/message/message";
import {NotificationService} from "../notification/notification.service";
import {TranslateService} from "@ngx-translate/core";
import {OrderType} from '../order/order';
import {OrderModel} from '../order/order.model';
import {IdentityData} from '../identity/identity-entity.model';
import {AddressData} from '../customer-info/address.data';
import {CustomerDetails, CustomerDetailsService} from '../customer-info/customer-details.service';
import {ActiveSchedulesSearchModel} from '../schedule/active-schedules-search.model';
import {DealData} from '../deal/deal-entity.model';
import {DealModel} from '../deal/deal.model';
import {MenuModel} from '../menu/menu.model';
import {LoadingService} from "../ui-elements/loading/loading.service";
import {AppDeviceService} from '../app-device/app-device.service';
import {GeolocateService} from '../store/geolocate';
import {whenSignalMatches} from '../utils/signal-utils';
import {DeferredInjectable} from '../deferred-injection.service';
import {IdentityModel} from '../identity/identity.model';
import {DistanceResult, ExternalApiBridgeService, LatLng} from '../external-api/external-api-bridge.service';

export class StoreLocation {
    // noinspection JSUnusedGlobalSymbols
    constructor(public storeName: string, public locationName: string, public stores: StoreData[]) {
    }

    /**
     * Returns the store at this store-location which supports the specified order type.
     * Duplicate stores for an order type are not supported - only one will be returned.
     *
     * @param orderType
     */
    getStoreForOrderType(orderType: OrderType): StoreData {
        return orderType ? this.stores.find((store: StoreData) => store.canOrder(orderType)) : undefined;
    }
}

export class OrderingContextStoreSearchFilterProvider implements FilterProvider {
    private orderingContextModel: OrderingContextModel;

    public static readonly FILTER_IGNORE_EXCLUSIVE: string = "ignoreExclusive";

    public inject(): void {
        this.orderingContextModel = inject(OrderingContextModel);
    }

    getFilters(filters: any): any {
        const newFilters: any = {};
        if (this.orderingContextModel) {
            if (!filters[OrderingContextStoreSearchFilterProvider.FILTER_IGNORE_EXCLUSIVE]) {
                if (this.orderingContextModel.exclusiveStoreId()) {
                    newFilters[StoreSearchModel.FILTER_SINGLE_STORE_ID] = this.orderingContextModel.exclusiveStoreId();
                } else if (this.orderingContextModel.exclusiveStoreGroup()) {
                    newFilters[StoreSearchModel.FILTER_STORE_GROUP] = this.orderingContextModel.exclusiveStoreGroup();
                } else if (this.orderingContextModel.exclusiveStoreIdList()) {
                    newFilters[StoreSearchModel.FILTER_STORES] = this.orderingContextModel.exclusiveStoreIdList();
                }
            }

            if (this.orderingContextModel.tableOnlyMode()) {
                newFilters[StoreSearchModel.FILTER_ORDER_AT_TABLE_ONLY] = 1;
                newFilters[StoreSearchModel.FILTER_DISABLE_OTHER_ORDER_TYPES] = 1;
            }

            if (this.orderingContextModel.pickupOnlyMode()) {
                newFilters[StoreSearchModel.FILTER_ORDER_AHEAD_ONLY] = 1;
                newFilters[StoreSearchModel.FILTER_DISABLE_OTHER_ORDER_TYPES] = 1;
            }

            if (this.orderingContextModel.deliveryOnlyMode()) {
                newFilters[StoreSearchModel.FILTER_ORDER_DELIVERY_ONLY] = 1;
                newFilters[StoreSearchModel.FILTER_DISABLE_OTHER_ORDER_TYPES] = 1;
            }

            if (this.orderingContextModel.parentAppURL() && !filters[StoreSearchModel.FILTER_TABLE_LOOKUP_CODE]) {
                newFilters[StoreSearchModel.FILTER_PARENT_APP_URL] = this.orderingContextModel.parentAppURL();
                newFilters[StoreSearchModel.FILTER_LOCAL_APP_URL] = window.location.href.split("?")[0];
            }
        }
        return newFilters;
    }
}

@Injectable({providedIn: "root"})
export class OrderingContextModel implements DeferredInjectable {
    private injector: Injector = inject(Injector);
    private configurationModel: ConfigurationModel = inject(ConfigurationModel);
    private sessionModel: SessionModel = inject(SessionModel);
    private storeSearchModel: StoreSearchModel = inject(StoreSearchModel);
    private activeSchedulesSearchModel: ActiveSchedulesSearchModel = inject(ActiveSchedulesSearchModel);
    private navigationService: NavigationService = inject(NavigationService);
    private notificationService: NotificationService = inject(NotificationService);
    private loadingService: LoadingService = inject(LoadingService);
    private translate: TranslateService = inject(TranslateService);
    private authService: AuthService = inject(AuthService);
    private customerDetailsService: CustomerDetailsService = inject(CustomerDetailsService);
    private appDeviceService: AppDeviceService = inject(AppDeviceService);
    private geolocateService: GeolocateService = inject(GeolocateService);
    private analyticsService: AnalyticsService = inject(AnalyticsService);
    private orderModel: OrderModel;
    private menuModel: MenuModel;
    private dealModel: DealModel;
    private identityModel: IdentityModel;

    /**
     * Selected ordering context
     */
    public readonly orderType: WritableSignal<OrderType> = signal<OrderType>(undefined);
    public readonly marketingOptIn: WritableSignal<boolean> = signal<boolean>(undefined);
    private readonly _parentAppURL: WritableSignal<string> = signal<string>(undefined);
    public readonly parentAppURL: Signal<string> = this._parentAppURL.asReadonly();
    private readonly _singleOrderType: WritableSignal<OrderType> = signal<OrderType>(undefined);
    public readonly singleOrderType: Signal<OrderType> = this._singleOrderType.asReadonly();
    public readonly stores: WritableSignal<StoreData[]> = signal<StoreData[]>(undefined);
    public readonly deliveryStores: WritableSignal<StoreData[]> = signal<StoreData[]>(undefined);
    private readonly _currentStore: WritableSignal<StoreData> = signal<StoreData>(undefined);
    public readonly currentStore: Signal<StoreData> = this._currentStore.asReadonly();
    private readonly _currentStoreLocation: WritableSignal<StoreLocation> = signal<StoreLocation>(undefined);
    public readonly currentStoreLocation: Signal<StoreLocation> = this._currentStoreLocation.asReadonly();
    private readonly _singleStoreLocation: WritableSignal<StoreLocation> = signal<StoreLocation>(undefined);
    public readonly singleStoreLocation: Signal<StoreLocation> = this._singleStoreLocation.asReadonly();

    /**
     * Preselected ordering context derived from url params
     */
    private readonly preselectedOrderType: WritableSignal<OrderType> = signal<OrderType>(undefined);
    private readonly preselectedDealCode: WritableSignal<string> = signal<string>(undefined);
    private readonly preselectedStoreId: WritableSignal<number> = signal<number>(undefined);
    private readonly preselectedStoreIdList: WritableSignal<number[]> = signal<number[]>(undefined);
    private readonly preselectedStoreGroup: WritableSignal<string> = signal<string>(undefined);
    private readonly preselectedTableNumber: WritableSignal<string> = signal<string>(undefined);
    private readonly preselectedTableLookupCode: WritableSignal<string> = signal<string>(undefined);

    private readonly _tableOnlyMode: WritableSignal<boolean> = signal<boolean>(undefined);
    public readonly tableOnlyMode: Signal<boolean> = this._tableOnlyMode.asReadonly();
    private readonly _pickupOnlyMode: WritableSignal<boolean> = signal<boolean>(undefined);
    public readonly pickupOnlyMode: Signal<boolean> = this._pickupOnlyMode.asReadonly();
    private readonly _deliveryOnlyMode: WritableSignal<boolean> = signal<boolean>(undefined);
    public readonly deliveryOnlyMode: Signal<boolean> = this._deliveryOnlyMode.asReadonly();

    private readonly _exclusiveStoreGroup: WritableSignal<string> = signal<string>(undefined);
    public readonly exclusiveStoreGroup: Signal<string> = this._exclusiveStoreGroup.asReadonly();
    private readonly _exclusiveStoreId: WritableSignal<number> = signal<number>(undefined);
    public readonly exclusiveStoreId: Signal<number> = this._exclusiveStoreId.asReadonly();
    private readonly _exclusiveStoreIdList: WritableSignal<number[]> = signal<number[]>(undefined);
    public readonly exclusiveStoreIdList: Signal<number[]> = this._exclusiveStoreIdList.asReadonly();

    private readonly useStoreCoords: WritableSignal<boolean> = signal<boolean>(undefined);
    private readonly latitude: WritableSignal<number> = signal<number>(undefined);
    private readonly longitude: WritableSignal<number> = signal<number>(undefined);

    /**
     * All tables across all stores
     */
    private readonly _tables: WritableSignal<TableData[]> = signal<TableData[]>([]);
    public readonly tables: Signal<TableData[]> = this._tables.asReadonly();

    // This is stored as a date object for convenience and formatting flexibility.
    // However, it DOES NOT properly represent an instant in time as in seconds since epoch.
    // Rather, it represents a local-date/time in the timezone of the relevant store, which has been parsed AS IF it were in the local timezone.
    // This means that when this date is converted back to a string, whether for display to the user, or for sending to the server,
    // it should always be in a timezone-independent format. It will implicitly be in the time-zone of the relevant store.
    // It also means that any modification to this date (e.g. setting the selected time) should be done using values which themselves are
    // also in the same implicit timezone.
    // Furthermore, this date should never be used to compare against a local date.
    public readonly expectedCompletion: WritableSignal<Date> = signal<Date>(undefined);
    private readonly _evaluatingExpectedCompletionChange: WritableSignal<boolean> = signal(undefined);

    // Tracks the fact that we are assessing if there are any impacts to the ordered items after an order time change.
    public readonly evaluatingExpectedCompletionChange: Signal<boolean> = this._evaluatingExpectedCompletionChange.asReadonly();

    // Customer details and other order-meta-data are defaulted from the logged-in member, but can be modified for the specific order.
    // Theoretically, customer notes can be stored on a member, but the app does not use them in this way at present. They are always set only for a single order.
    public readonly firstName: WritableSignal<string> = signal<string>(undefined);
    public readonly lastName: WritableSignal<string> = signal<string>(undefined);
    public readonly email: WritableSignal<string> = signal<string>(undefined);
    public readonly mobilePhone: WritableSignal<string> = signal<string>(undefined);
    public readonly deliveryInstructions: WritableSignal<string> = signal<string>(undefined);
    public readonly address: WritableSignal<AddressData> = signal<AddressData>(undefined);

    public readonly firstNameTrusted: WritableSignal<boolean> = signal<boolean>(false);
    public readonly lastNameTrusted: WritableSignal<boolean> = signal<boolean>(false);
    public readonly emailTrusted: WritableSignal<boolean> = signal<boolean>(false);
    public readonly mobilePhoneTrusted: WritableSignal<boolean> = signal<boolean>(false);
    public readonly deliveryInstructionsTrusted: WritableSignal<boolean> = signal<boolean>(false);
    public readonly addressTrusted: WritableSignal<boolean> = signal<boolean>(false);

    private readonly _saveAddressForMember: WritableSignal<boolean> = signal<boolean>(undefined);
    public readonly saveAddressForMember: Signal<boolean> = this._saveAddressForMember.asReadonly();
    private readonly _showMarketingOptIn: WritableSignal<boolean> = signal<boolean>(false);
    public readonly showMarketingOptIn: Signal<boolean> = this._showMarketingOptIn.asReadonly();
    public readonly tipSelected: WritableSignal<boolean> = signal<boolean>(undefined);

    public readonly trimmedFirstName: Signal<string> = computed(() => this.firstName()?.trim());
    public readonly trimmedLastName: Signal<string> = computed(() => this.lastName()?.trim());

    public readonly preselectedTableSelection: Signal<TableSelection> = computed<TableSelection>(() =>
        this.preselectedTableLookupCode() || this.preselectedTableNumber() || this.preselectedStoreGroup() || this.preselectedStoreIdList() ? {
            tableLookupCode: this.preselectedTableLookupCode(),
            tableNumber: this.preselectedTableNumber(),
            storeGroup: this.preselectedStoreGroup(),
            storeIds: this.preselectedStoreIdList()
        } : undefined);

    public readonly currentTableNumberLabel: Signal<string> = computed(() =>
        this.orderType() !== OrderType.ORDER_AT_TABLE ? undefined
            : this._currentStore() && this._currentStore().table.tableNumber != undefined ? this._currentStore().table.tableNumber
                : this.preselectedTableNumber() ? this.preselectedTableNumber()
                    : undefined);

    public readonly hasPreselectedTable: Signal<boolean> = computed<boolean>(() => !!this.preselectedTableLookupCode());
    public readonly menuId: Signal<number> = computed<number>(() => this._currentStore()?.menuId ?? this.configurationModel.app()?.defaultMenuId);
    public readonly horizontalLogoImage: Signal<string> = computed(() => this._currentStore()?.horizontalLogoImage ?? this.configurationModel.app()?.horizontalLogoImage);
    public readonly horizontalLogoImageSvg: Signal<string> = computed(() => this._currentStore()?.horizontalLogoImage ? this._currentStore().horizontalLogoImageSvg : this.configurationModel.app()?.horizontalLogoImageSvg);
    public readonly horizontalLogoHeightPx: Signal<number> = computed<number>(() => (this._currentStore()?.horizontalLogoImage && this._currentStore().horizontalLogoHeightPx) ?? this.configurationModel.app()?.horizontalLogoHeightPx);
    public readonly verticalLogoImage: Signal<string> = computed(() => this._currentStore()?.verticalLogoImage ?? this.configurationModel.app()?.verticalLogoImage);
    public readonly verticalLogoImageSvg: Signal<string> = computed(() => this._currentStore()?.verticalLogoImage ? this._currentStore().verticalLogoImageSvg : this.configurationModel.app()?.verticalLogoImageSvg);
    public readonly verticalLogoWidthPx: Signal<number> = computed<number>(() => (this._currentStore()?.verticalLogoImage && this._currentStore().verticalLogoWidthPx) ?? this.configurationModel.app()?.verticalLogoWidthPx);
    public readonly singleStore: Signal<boolean> = computed<boolean>(() => !this.stores() || (this._singleStoreLocation() && this._singleStoreLocation().stores.length === 1));
    public readonly hasStoreGroup: Signal<boolean> = computed<boolean>(() => !!this._exclusiveStoreGroup() || !!this.preselectedStoreGroup() || !!this._exclusiveStoreIdList() || !!this.preselectedStoreIdList());
    public readonly canChangeStore: Signal<boolean> = computed<boolean>(() => !this._singleStoreLocation() && !(this._currentStore() && this.orderType() === OrderType.ORDER_AT_TABLE && !this.canChangeTableStore()));
    public readonly canChangeTableStore: Signal<boolean> = computed<boolean>(() => this.hasPreselectedTable() && (!!this.preselectedStoreIdList() || !!this.preselectedStoreGroup()));
    public readonly isConfirmedTableOrdering: Signal<boolean> = computed<boolean>(() => this.orderType() === OrderType.ORDER_AT_TABLE && this._currentStore() && !!this._currentStore().table.tableNumber);
    public readonly isConfirmedDeliveryOrdering: Signal<boolean> = computed<boolean>(() => this.orderType() === OrderType.ORDER_DELIVERY && this.addressTrusted());
    public readonly fulfilmentDetailsRequired: Signal<boolean> = computed<boolean>(() => (this.orderType() === OrderType.ORDER_AT_TABLE && !this._currentStore().table.tableNumber) || (this.orderType() === OrderType.ORDER_DELIVERY && !this.addressTrusted()));
    public readonly expectedCompletionAsString: Signal<string> = computed(() => this.expectedCompletion() ? EntityData.formatLocalDateTime(this.expectedCompletion()) : null);

    public readonly formattedExpectedCompletion: Signal<string> = computed(() =>
        this.expectedCompletion() ? this.configurationModel.relativeDateAndTime()(formatDateForDisplay(this.expectedCompletion(), false), formatTimeForDisplay(this.expectedCompletion())) : undefined);

    public readonly isStoreOpenAtCompletionTime: Signal<boolean> = computed<boolean>(() =>
        this._currentStore() && this.expectedCompletion() ?
            this.expectedCompletion() >= EntityData.parseLocalDateTime(this._currentStore().systemStartLocalDateTime) && this.expectedCompletion() < EntityData.parseLocalDateTime(this._currentStore().systemEndLocalDateTime)
            : true);

    public readonly canSelectOrderTime: Signal<boolean> = computed<boolean>(() =>
        this.orderType() !== OrderType.ORDER_AT_TABLE && this._singleOrderType() !== OrderType.ORDER_AT_TABLE &&
        (this._currentStore() ? this._currentStore().orderAheadLaterTimeStore : (this.hasAllStoresWithOrderLaterTime() && !this.hasStoresInMultipleTimeZones())));

    public readonly canShowAsapSelectionTime: Signal<boolean> = computed<boolean>(() => this.configurationModel.app()?.showAsapSelectionTime || (this._currentStore() && !this._currentStore().orderAheadLaterTimeStore));
    public readonly canShowETA: Signal<boolean> = computed<boolean>(() => this.configurationModel.app()?.showAsapSelectionTime || (this._currentStore() ? this._currentStore().orderAheadLaterTimeStore : this.hasAllStoresWithOrderLaterTime()));
    public readonly integratedDelivery: Signal<boolean> = computed<boolean>(() => this.orderType() === OrderType.ORDER_DELIVERY && this._currentStore() && this._currentStore().integratedDelivery);
    public readonly showWaitTimeNotices: Signal<boolean> = computed<boolean>(() => this.orderType() === OrderType.ORDER_AT_TABLE || !this.expectedCompletion());
    public readonly showVenueWaitTimeNotice: Signal<boolean> = computed<boolean>(() => this._currentStore() && !this._currentStore().closed && !!this.venueWaitTime() && this.showWaitTimeNotices());
    public readonly venueWaitTime: Signal<number> = computed<number>(() => !this._currentStore() || (this._currentStore().currentWaitTimeMinutes || 0) <= (this._currentStore().completionTime || 0) ? 0 : this._currentStore().currentWaitTimeMinutes);

    public readonly headerImage: Signal<FedeltaImageData> = computed<FedeltaImageData>(() =>
        this.currentStoreHasImage() ? this._currentStore().image
            : this.configurationModel.app()?.orderingImage?.id ? this.configurationModel.app()?.orderingImage
                : undefined);

    public readonly headerImageOpacity: Signal<number> = computed<number>(() =>
        this.currentStoreHasImage() ? this._currentStore().imageOpacity
            : this.configurationModel.app() ? this.configurationModel.app()!.orderingImageOpacity
                : 1.0);

    public readonly hasHeaderImage: Signal<boolean> = computed<boolean>(() => this.currentStoreHasImage() || !!(this.configurationModel.app()?.orderingImage && (this.configurationModel.app()!.orderingImage.id || this.configurationModel.app()!.orderingImage.image)));
    public readonly currentStoreHasImage: Signal<boolean> = computed<boolean>(() => !!(this._currentStore() && this._currentStore().image && (this._currentStore().image.id || this._currentStore().image.image)));
    private readonly hasStoresInMultipleTimeZones: Signal<boolean> = computed<boolean>(() => this.stores() ? new Set(this.stores().map((store: StoreData) => store.timeZone)).size > 1 : false);

    public readonly showPhoneNumber: Signal<boolean> = computed<boolean>(() =>
        !this.configurationModel.app() ? false
            : this.configurationModel.isWaiterActor() ? this.configurationModel.app()!.showPhoneNumberForWaiter
                : this.orderType() === OrderType.ORDER_AHEAD ? this.configurationModel.app()!.showPhoneNumberForPickup
                    : this.orderType() === OrderType.ORDER_DELIVERY ? this.configurationModel.app()!.showPhoneNumberForDelivery || (this._currentStore() && this._currentStore().integratedDelivery)
                        : this.orderType() === OrderType.ORDER_AT_TABLE ? this.configurationModel.app()!.showPhoneNumberForTable
                            : false);

    public readonly showEmail: Signal<boolean> = computed<boolean>(() =>
        !this.configurationModel.app() ? false
            : this.configurationModel.isWaiterActor() ? this.configurationModel.app()!.showEmailForWaiter
                : this.orderType() === OrderType.ORDER_AHEAD ? this.configurationModel.app()!.showEmailForPickup
                    : this.orderType() === OrderType.ORDER_DELIVERY ? this.configurationModel.app()!.showEmailForDelivery || (this._currentStore() && this._currentStore().integratedDelivery)
                        : this.orderType() === OrderType.ORDER_AT_TABLE ? this.configurationModel.app()!.showEmailForTable
                            : false);

    public readonly showName: Signal<boolean> = computed<boolean>(() =>
        !this.configurationModel.app() ? false
            : this.configurationModel.isWaiterActor() ? this.configurationModel.app()!.showNameForWaiter
                : this.orderType() === OrderType.ORDER_AHEAD ? this.configurationModel.app()!.showNameForPickup
                    : this.orderType() === OrderType.ORDER_DELIVERY ? this.configurationModel.app()!.showNameForDelivery || (this._currentStore() && this._currentStore().integratedDelivery)
                        : this.orderType() === OrderType.ORDER_AT_TABLE ? this.configurationModel.app()!.showNameForTable
                            : false);

    public readonly requirePhoneNumber: Signal<boolean> = computed<boolean>(() =>
        !this.showPhoneNumber() ? false
            : !this.configurationModel.app() ? false
                : this.configurationModel.isWaiterActor() ? this.configurationModel.app()!.requirePhoneNumberForWaiter
                    : this.orderType() === OrderType.ORDER_AHEAD ? this.configurationModel.app()!.requirePhoneNumberForPickup
                        : this.orderType() === OrderType.ORDER_DELIVERY ? this.configurationModel.app()!.requirePhoneNumberForDelivery || (this._currentStore() && this._currentStore().integratedDelivery)
                            : this.orderType() === OrderType.ORDER_AT_TABLE ? this.configurationModel.app()!.requirePhoneNumberForTable
                                : false);

    public readonly requireEmail: Signal<boolean> = computed<boolean>(() =>
        !this.showEmail() ? false
            : !this.configurationModel.app() ? false
                : this.configurationModel.isWaiterActor() ? this.configurationModel.app()!.requireEmailForWaiter
                    : this.orderType() === OrderType.ORDER_AHEAD ? this.configurationModel.app()!.requireEmailForPickup
                        : this.orderType() === OrderType.ORDER_DELIVERY ? this.configurationModel.app()!.requireEmailForDelivery || (this._currentStore() && this._currentStore().integratedDelivery)
                            : this.orderType() === OrderType.ORDER_AT_TABLE ? this.configurationModel.app()!.requireEmailForTable
                                : false);

    public readonly requireFirstName: Signal<boolean> = computed<boolean>(() =>
        !this.showName() ? false
            : !this.configurationModel.app() ? false
                : this.configurationModel.isWaiterActor() ? this.configurationModel.app()!.requireFirstNameForWaiter
                    : this.orderType() === OrderType.ORDER_AHEAD ? this.configurationModel.app()!.requireFirstNameForPickup
                        : this.orderType() === OrderType.ORDER_DELIVERY ? this.configurationModel.app()!.requireFirstNameForDelivery || (this._currentStore() && this._currentStore().integratedDelivery)
                            : this.orderType() === OrderType.ORDER_AT_TABLE ? this.configurationModel.app()!.requireFirstNameForTable
                                : false);

    public readonly requireLastName: Signal<boolean> = computed<boolean>(() =>
        !this.showName() ? false
            : this.orderType() === OrderType.ORDER_DELIVERY ? this._currentStore().integratedDelivery
                : false);

    /**
     * Notifies that selected store details have changed.
     * The value indicates whether the store itself has changed (as opposed to just the table).
     */
    public readonly storeDetailsChanged$: Subject<boolean> = new Subject();

    constructor() {
        console.log("--- OrderingContextModel");

        void this.initConfig();

        onEach(this.sessionModel.sessionReset$, (sessionReset: SessionReset) => this.reset(sessionReset));

        effect(async () => {
            if (!this.configurationModel.app()) {
                return;
            }
            const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
            if (paramConfig.pickupOnlyMode && this.configurationModel.app().appSettings.find((setting: AppSettingData) => setting.name === "pickupOnlyImpliesDeliveryOnly" && setting.value === "true")) {
                paramConfig.deliveryOnlyMode = true;
                this._deliveryOnlyMode.set(true);
            }
        });
    }

    public inject(): void {
        this.orderModel = inject(OrderModel);
        this.menuModel = inject(MenuModel);
        this.dealModel = inject(DealModel);
        this.identityModel = inject(IdentityModel);
    }

    /**
     * Populates the ordering context from the param config.
     * @private
     */
    private async initConfig(): Promise<void> {
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        if (!paramConfig.unknownBeacon && !paramConfig.beaconLookupFailed) {
            if (paramConfig.exclusiveStore) {
                this._exclusiveStoreId.set(paramConfig.exclusiveStore);
                this.preselectedStoreId.set(paramConfig.exclusiveStore);
            } else {
                this._parentAppURL.set(paramConfig.parentAppURL);
                if (paramConfig.store) {
                    this.preselectedStoreId.set(paramConfig.store);
                }
                if (paramConfig.exclusiveStoreGroup) {
                    this._exclusiveStoreGroup.set(paramConfig.exclusiveStoreGroup);
                    this.preselectedStoreGroup.set(paramConfig.exclusiveStoreGroup);
                } else if (paramConfig.exclusiveStores) {
                    this._exclusiveStoreIdList.set(paramConfig.exclusiveStores);
                    this.preselectedStoreIdList.set(paramConfig.exclusiveStores);
                } else if (paramConfig.storeGroup) {
                    this.preselectedStoreGroup.set(paramConfig.storeGroup);
                } else if (paramConfig.stores) {
                    this.preselectedStoreIdList.set(paramConfig.stores);
                }
            }

            this._tableOnlyMode.set(paramConfig.tableOnlyMode);
            this._pickupOnlyMode.set(paramConfig.pickupOnlyMode);
            this._deliveryOnlyMode.set(paramConfig.deliveryOnlyMode);

            this.useStoreCoords.set(paramConfig.useStoreCoords);
            this.latitude.set(paramConfig.latitude);
            this.longitude.set(paramConfig.longitude);

            this.preselectedDealCode.set(paramConfig.dealCode);

            await this.setPreselectedTableAndOrderTypeFromParamConfig(paramConfig, new SessionReset());
        }
    }

    /**
     * Sets/resets the preselected table and order type from the param config.
     * If paramConfig not specified (when called from reset()), and if table retention is not specified in sessionReset.
     * the param config will be reinitialised (reloaded from the URL params), which includes a lookup of beacon details,
     * thus causing any table selection made by the user within the app to be cleared in the configurationModel.
     *
     * @param paramConfig
     * @param sessionReset
     * @private
     */
    private async setPreselectedTableAndOrderTypeFromParamConfig(paramConfig: ParamConfig, sessionReset: SessionReset): Promise<void> {
        if (!paramConfig) {
            paramConfig = await this.configurationModel.getParamConfig(!sessionReset.retainTable);
        }

        // Order type parameter is only valid when a specific store is preselected
        const orderTypeSpecified: boolean = !!this.preselectedStoreId() && !!paramConfig.orderType;

        // Table lookup code parameter is only valid when one or more stores are preselected or table-only-mode is specified
        if ((this.preselectedStoreId() || this.preselectedStoreIdList() || this.preselectedStoreGroup() || this._tableOnlyMode()) && paramConfig.tableLookupCode) {
            if (!sessionReset.retainTable) {
                this.preselectedTableLookupCode.set(paramConfig.tableLookupCode);
            }
            if (!orderTypeSpecified && !this._pickupOnlyMode() && !this._deliveryOnlyMode() && !sessionReset.retainOrderType) {
                this.preselectedOrderType.set(OrderType.ORDER_AT_TABLE);
            }
        }
        if (orderTypeSpecified && !sessionReset.retainOrderType) {
            this.preselectedOrderType.set(paramConfig.orderType);
        }
    }

    /**
     * Reset the ordering context.
     * If table and/or order type are not specified to be retained by the sessionReset parameter,
     * the preselected table and order type will be reset to their original values from the param config.
     *
     * @param sessionReset Specifies whether each of store, table, and order type should be retained.
     * @private
     */
    private async reset(sessionReset: SessionReset): Promise<void> {
        if (!sessionReset.retainTable) {
            if (this._currentStore()) {
                this._currentStore().table = undefined;
            }
            this.preselectedTableLookupCode.set(undefined);
            this.preselectedTableNumber.set(undefined);
        }
        if (!sessionReset.retainStore) {
            this._currentStore.set(undefined);
            this._currentStoreLocation.set(undefined);
            if (this.authService.isLoggedIn()) {
                this.authService.clearLocationContext();
            }
        }
        if (!sessionReset.retainOrderType) {
            this.orderType.set(undefined);
        }

        await this.setPreselectedTableAndOrderTypeFromParamConfig(undefined, sessionReset);

        if (this.configurationModel.isWaiterActor()) {
            this.firstName.set(undefined);
            this.firstNameTrusted.set(false);
            this.lastName.set(undefined);
            this.lastNameTrusted.set(false);
            this.email.set(undefined);
            this.emailTrusted.set(false);
            this.mobilePhone.set(undefined);
            this.mobilePhoneTrusted.set(false);
        }

        this.stores.set(undefined);
        this._singleStoreLocation.set(undefined);
        this.deliveryStores.set(undefined);

        this.deliveryInstructions.set(undefined);
        this.address.set(undefined);
        this.addressTrusted.set(false);
        this.deliveryInstructionsTrusted.set(false);
        this.expectedCompletion.set(undefined);
        this._saveAddressForMember.set(false);
        this._showMarketingOptIn.set(false);
        this.marketingOptIn.set(undefined);
        this.tipSelected.set(false);
    }

    public getAndClearPreselectedDealCode(): string {
        const code: string = this.preselectedDealCode();
        this.preselectedDealCode.set(undefined);
        return code;
    }


    public canShowETAForStore(store: StoreData): boolean {
        return this.configurationModel.app()?.showAsapSelectionTime || store.orderAheadLaterTimeStore;
    }

    public hasAnyStoresWithOrderLaterTime(stores: StoreData[] = this.stores()): boolean {
        return !!stores && stores.some((store) => store.orderAheadLaterTimeStore);
    }

    private hasAllStoresWithOrderLaterTime(stores: StoreData[] = this.stores()): boolean {
        return !!stores && stores.every((store) => store.orderAheadLaterTimeStore);
    }

    public hasAnyStoresWithOrderLater(stores: StoreData[] = this.stores()): boolean {
        return !!stores && stores.find((store) => store.orderAheadLaterStore) != undefined;
    }

    public hasAnyStoresWithState(stores: StoreData[] = this.stores()): boolean {
        return !!stores && stores.find((store) => !!store.state) != undefined;
    }

    public getStoresForTable(tableNumber: string): StoreData[] {
        const stores: StoreData[] = [];
        this.stores().forEach((store: StoreData) => {
            if (store.availableTables.find((table: TableData) => table.tableLookupCode === tableNumber)) {
                stores.push(store);
            }
        });

        return stores;
    }

    public async loadStores(force: boolean = false): Promise<void> {
        if (this.stores() && !force) {
            return;
        }

        try {
            if (this.appDeviceService.deviceLocationId()) {
                this.storeSearchModel.setFilterValue(StoreSearchModel.FILTER_LOCATION_ID, this.appDeviceService.deviceLocationId());
            }
            this.storeSearchModel.filter[StoreSearchModel.FILTER_ORDER_LOCAL_DATE_TIME] = this.expectedCompletionAsString();
            await this.storeSearchModel.retrieve();
        } catch (error) {
            console.error(error);
            return;
        }

        this.stores.set(this.storeSearchModel.dataSignal());
        this._singleStoreLocation.set(undefined);
        this._singleOrderType.set(undefined);

        // Merge all tables from the stores
        const tableMap: Map<String, TableData> = new Map();
        // Remove duplicates by tableNumber
        for (const store of this.stores()) {
            for (const table of store.availableTables) {
                if (!tableMap.has(table.tableLookupCode)) {
                    tableMap.set(table.tableLookupCode, table);
                }
            }
        }

        const uniqueTables: TableData[] = Array.from(tableMap.values());
        uniqueTables.sort((a: TableData, b: TableData) => a.compareTable(b));
        this._tables.set(uniqueTables);

        // Determine if there is a single store-location.
        let locations: string[] = this.stores().map((store: StoreData) => store.locationName);
        locations = locations.filter((locationName: string, pos: number) => locations.indexOf(locationName) === pos);
        if (locations.length === 1) {
            let storeNames: string[] = this.stores().map((store: StoreData) => store.collectiveStoreName);
            storeNames = storeNames.filter((storeName: string, pos: number) => storeNames.indexOf(storeName) === pos);
            if (storeNames.length === 1) {

                // It is not considered valid to define multiple stores with the same name at the same location for a given order type.
                // We discard any store which provides all order types earlier in the list.
                // This allows easier detection of the effective single store case where multiple stores are defined for a store-location,
                // but they are indistinguishable.
                // Note that this does not guarantee that no two stores provide the same order type, but rather that each store
                // provides at least one unique order type. The getStoreForOrderType of StoreLocation selects the first store for a given order type.
                // Note also that for multi-store, this filtering of stores does not currently occur, as it is more about ensuring
                // proper handling of single stores.

                const distinctStores: StoreData[] = this.stores().filter((store: StoreData, pos: number) =>
                    (store.orderAheadStore && this.stores().findIndex((otherStore: StoreData) => otherStore.orderAheadStore) === pos) ||
                    (store.orderDeliveryStore && this.stores().findIndex((otherStore: StoreData) => otherStore.orderDeliveryStore) === pos) ||
                    (store.orderAtTableStore && this.stores().findIndex((otherStore: StoreData) => otherStore.orderAtTableStore) === pos));
                this._singleStoreLocation.set(new StoreLocation(this.stores()[0].collectiveStoreName, locations[0], distinctStores));
            }
        }

        let selectedStore: StoreData = undefined;

        // Reselect the current store if any
        if (this._currentStore()) {
            selectedStore = this.stores().find((store) => store.storeId === this._currentStore().storeId);
        } else {
            // Auto-select the preselected order type, store, and optionally the table
            if (this.preselectedStoreId()) {
                selectedStore = this.stores().find((store) => store.storeId === this.preselectedStoreId());
                if (selectedStore) {
                    selectedStore = await this.applyPreselectedTableToSelectedStore(selectedStore);
                }
            } else if (this.hasPreselectedTable() && !this._pickupOnlyMode() && !this._deliveryOnlyMode()) {
                this.orderType.set(OrderType.ORDER_AT_TABLE);

                // Even though there are multiple stores, and any custom table number could theoretically be different for each one,
                // we assume for now that they are likely to be the same and that any mapping is better than none.
                // Once a store is selected, the custom lookup will be done again to make sure the mapping is correct.
                const lookupStore: StoreData = await this.lookupCustomTableNumber(this.preselectedTableLookupCode(), this.preselectedStoreGroup(), this.stores()[0].storeId);
                if (lookupStore && lookupStore.table.tableNumber) {
                    this.preselectedTableNumber.set(lookupStore.table.tableNumber);
                } else if (!this.preselectedTableNumber()) {
                    this.preselectedTableNumber.set(await this.getDefaultTableNumber(this.preselectedTableLookupCode()));
                }
            }

            // Auto-select an actual single store
            if (!selectedStore && this.singleStore()) {
                selectedStore = await this.applyPreselectedTableToSelectedStore(this.stores()[0]);
            }

            // If the auto-selected store has a single order type, auto-select that also.
            if (selectedStore && !this.orderType()) {
                if (selectedStore.orderAheadStore && !selectedStore.orderDeliveryStore && !selectedStore.orderAtTableStore) {
                    this.orderType.set(OrderType.ORDER_AHEAD);
                } else if (selectedStore.orderDeliveryStore && !selectedStore.orderAheadStore && !selectedStore.orderAtTableStore) {
                    this.orderType.set(OrderType.ORDER_DELIVERY);
                } else if (selectedStore.orderAtTableStore && !selectedStore.orderAheadStore && !selectedStore.orderDeliveryStore) {
                    this.orderType.set(OrderType.ORDER_AT_TABLE);
                } else if ((this.orderType() === OrderType.ORDER_AHEAD && !selectedStore.orderAheadStore) ||
                    (this.orderType() === OrderType.ORDER_DELIVERY && !selectedStore.orderDeliveryStore) ||
                    (this.orderType() === OrderType.ORDER_AT_TABLE && !selectedStore.orderAtTableStore)) {
                    this.orderType.set(undefined);
                }
            }
        }

        // If we have a single store-location, and we know the order type, we can auto-select the store
        if (!selectedStore && this._singleStoreLocation() && this.orderType()) {
            selectedStore = this._singleStoreLocation().getStoreForOrderType(this.orderType());
        }

        // Retain the selected table code if the store is unchanged
        if (selectedStore && this._currentStore() && this._currentStore().storeId === selectedStore.storeId && this._currentStore().table.tableNumber) {
            const store: StoreData = <StoreData>EntityData.create(StoreData).assign(selectedStore);
            store.table.assign(this._currentStore().table);
            if (await this.routeToExternalStore(store, OrderType.ORDER_AT_TABLE)) {
                return;
            }
            this._currentStore.set(store);
            this.orderType.set(OrderType.ORDER_AT_TABLE);
            this.updateCurrentStoreLocation();

            void this.sessionModel.notifyAppUsage(false);
        } else {
            if (selectedStore && await this.routeToExternalStore(selectedStore, this.orderType())) {
                return;
            }
            await this.setCurrentStore(selectedStore, this.orderType());
        }

        // Set the order type if there is only one option
        if (!this.storeSearchModel.hasMultipleStoreTypes()) {
            if (this.storeSearchModel.hasOrderAheadStores()) {
                this._singleOrderType.set(OrderType.ORDER_AHEAD);
            } else if (this.storeSearchModel.hasOrderDeliveryStores()) {
                this._singleOrderType.set(OrderType.ORDER_DELIVERY);
            } else if (this.storeSearchModel.hasOrderAtTableStores()) {
                this._singleOrderType.set(OrderType.ORDER_AT_TABLE);
            }
        }
        if (!this.orderType()) {
            this.orderType.set(this._singleOrderType());
        }

        if (!this._currentStore() && this.configurationModel.isConsumerActor()) {
            const position: GeolocationPosition = await this.getCurrentLocation();

            // The app may be cleared during current location retrieval - do not attempt loading of the nearest stores in that case.
            if (position && this.configurationModel.app()) {
                try {
                    void this.storeSearchModel.loadNearestStores(
                        this.configurationModel.app()!.storeLimit, this.configurationModel.app()!.storeRange, this.configurationModel.app()!.deliveryStoreRange,
                        position.coords.latitude, position.coords.longitude);
                } catch (error) {
                    console.log("Unable to load nearest stores", error);
                }
            }
        }
    }

    public async getCurrentLocation(timeout: number = 10000): Promise<GeolocationPosition> {
        return this.useStoreCoords() && this._currentStore() ? {
            coords: {
                latitude: this._currentStore().latitude,
                longitude: this._currentStore().longitude,
                accuracy: 0, altitude: null, altitudeAccuracy: null, heading: null, speed: null,
                toJSON: null
            },
            timestamp: Date.now(),
            toJSON: null
        } : this.useStoreCoords() && this._exclusiveStoreGroup() && this.stores().length ? {
            coords: {
                latitude: this.stores()[0].latitude,
                longitude: this.stores()[0].longitude,
                accuracy: 0, altitude: null, altitudeAccuracy: null, heading: null, speed: null,
                toJSON: null
            },
            timestamp: Date.now(),
            toJSON: null
        } : this.latitude() && this.longitude() ? {
            coords: {
                latitude: this.latitude(),
                longitude: this.longitude(),
                accuracy: 0, altitude: null, altitudeAccuracy: null, heading: null, speed: null,
                toJSON: null
            },
            timestamp: Date.now(),
            toJSON: null
        } : this.geolocateService.getCurrentLocation(timeout);
    }

    public async loadStoresForSelection(orderingContext: OrderingContext): Promise<boolean> {
        await this.configurationModel.loadApp();
        await this.loadStores(true);

        // No stores - go to the appropriate page, and it will inform the user
        if (!this.stores()) {
            void this.selectAndApplyContext(orderingContext.withIntention(OrderingContextIntention.ORDER));
            return false;
        }
        return true;
    }

    public async viewStores(): Promise<void> {
        return this.selectAndApplyContext(new OrderingContext()
            .withIntention(OrderingContextIntention.VIEW_STORES)
            .withSelectedOrderType(this.orderType())
            .withSelectedTable(this.preselectedTableSelection()));
    }

    /**
     * View the current store's information page
     */
    public async viewCurrentStore(): Promise<void> {
        if (!this._currentStore() && !this._singleStoreLocation()) {
            return;
        }
        return this.navigationService.createPage("woa-store-details-page", {
            orderingContext: new OrderingContext()
                .withIntention(OrderingContextIntention.VIEW_CURRENT_STORE)
                .withSelectedStore(this._currentStore())
        });
    }

    /**
     * Nearest stores for a delivery address are cached in the session model
     * to avoid the repeated overhead and cost of distance by road calculation.
     *
     * @param externalApiBridgeService
     * @param storeSearchModel
     * @param deliveryDetails
     * @returns an error message key if the specified delivery details are invalid for delivery, undefined otherwise.
     */
    public async findDeliveryStores(externalApiBridgeService: ExternalApiBridgeService, storeSearchModel: StoreSearchModel, deliveryDetails: DeliveryDetails): Promise<string> {
        if (!(deliveryDetails.latitude && deliveryDetails.longitude)) {
            this.deliveryStores.set([]);
            return undefined;
        }

        // Note that we don't use the delivery store limit here if using distance by road, because a store which is closer radially may be further away by road.
        // The assumption is that the general limit is higher, and will pick up more stores, which can be further refined below.
        const storeLimit: number = this.configurationModel.app()?.deliveryDistanceByRoad && this.configurationModel.app()!.storeLimit ? this.configurationModel.app()!.storeLimit : this.configurationModel.app()?.deliveryStoreLimit;
        console.log("Nearest store search:", storeLimit, deliveryDetails.latitude, deliveryDetails.longitude);
        storeSearchModel.findNearestStores(storeLimit,
            this.configurationModel.app()?.storeSearchRange, this.configurationModel.app()?.deliveryStoreRange,
            deliveryDetails.latitude, deliveryDetails.longitude, true);

        if (storeSearchModel.nearestStores().length == 0) {
            console.log("No nearest stores found");
            this.deliveryStores.set([]);
            return "woa.message.ordering-context.invalidDeliveryAddress";
        }

        if (this.configurationModel.app()?.deliveryDistanceByRoad) {
            const origin: LatLng = {
                lat: deliveryDetails.latitude,
                lng: deliveryDetails.longitude
            };
            const destinations: LatLng[] = storeSearchModel.nearestStores().map((store: StoreData) => ({
                lat: store.latitude,
                lng: store.longitude
            }));
            console.log("Delivery origin:", origin);
            console.log("Delivery destinations:", destinations);
            this.deliveryStores.set([]);
            try {
                const distances: DistanceResult[] = await externalApiBridgeService.getDistances(origin, destinations);

                console.log("Delivery distance lookup result:", distances);
                this.deliveryStores.set(StoreSearchModel.applyNearestStoreLimit(this.configurationModel.app()?.deliveryStoreLimit,
                    storeSearchModel.nearestStores()
                        .filter((store: StoreData, i: number) => {
                            store = <StoreData>EntityData.create(StoreData).assign(store);
                            const distanceResult: DistanceResult = distances[i];
                            // keep the radius if the distance by road could not be calculated
                            if (distanceResult.ok) {
                                store.distance = Math.round(distanceResult.distanceMeters / 100) / 10;
                            }
                            return !this.configurationModel.app()?.storeSearchRange || store.distance <= this.configurationModel.app()?.deliveryStoreRange;
                        })));
            } catch (e) {
                console.error("Geocoding failed", e);
            }
            return this.deliveryStores().length == 0 ? "woa.message.ordering-context.invalidDeliveryAddress" : undefined;
        }

        this.deliveryStores.set(StoreSearchModel.applyNearestStoreLimit(this.configurationModel.app()?.deliveryStoreLimit,
            storeSearchModel.nearestStores()
                .map((store: StoreData) => <StoreData>EntityData.create(StoreData).assign(store))
                .filter((store: StoreData) => !this.configurationModel.app()?.storeSearchRange || store.distance <= this.configurationModel.app()?.deliveryStoreRange)));
        return this.deliveryStores().length == 0 ? "woa.message.ordering-context.invalidDeliveryAddress" : undefined;
    }

    public async validateAndReapplyCurrentStore(): Promise<boolean> {
        if (!this._currentStore()) {
            return true;
        }

        await this.storeSearchModel.retrieve();
        if (!this.storeSearchModel.dataSignal()?.some((store: StoreData) => store.storeId === this._currentStore().storeId)) {
            if (this.orderModel.orderHasItems()) {
                await this.notificationService.displayAlert(undefined, "woa.message.identification.memberNotAvailableOrderedStore");
            }
            this._currentStore.set(undefined);
            this.orderModel.reset("store invalid", false);
            return false;
        }

        this.authService.setLocationContext(this._currentStore().locationId, this._currentStore().outletId, this._currentStore().storeId);
        return true;
    }

    public async lookupCustomTableNumber(tableLookupCode: string, storeGroup?: string, storeId?: number, ignoreExclusiveFilters: boolean = true, returnFirstMatch = false): Promise<StoreData> {
        try {
            const searchResult: SearchResult = await firstValueFrom(this.storeSearchModel.searchRecords({
                [OrderingContextStoreSearchFilterProvider.FILTER_IGNORE_EXCLUSIVE]: ignoreExclusiveFilters,
                [StoreSearchModel.FILTER_TABLE_LOOKUP_CODE]: tableLookupCode,
                [StoreSearchModel.FILTER_STORE_GROUP]: storeGroup,
                [StoreSearchModel.FILTER_ORDER_LOCAL_DATE_TIME]: this.expectedCompletionAsString()
            }, undefined, undefined, undefined));
            const matches: StoreData[] = storeId ? searchResult.data.filter((store: StoreData) => store.storeId === storeId) : searchResult.data;
            return matches.length === 1 || (matches.length > 1 && returnFirstMatch) ? matches[0] : undefined;
        } catch (error) {
            console.log("Custom table number lookup failed: ", error);
        }
        return undefined;
    }

    private async setCurrentStore(store: StoreData, orderType: OrderType): Promise<void> {
        if (this._currentStore() !== store) {
            const storeChanged: boolean = ((store == undefined) !== (this._currentStore() == undefined)) ||
                (store && this._currentStore() && store.storeId !== this._currentStore().storeId);
            let tableChanged: boolean = store && this._currentStore() && store.table.tableLookupCode !== this._currentStore().table.tableLookupCode;
            this._currentStore.set(store);

            if (store) {
                if (orderType !== OrderType.ORDER_AT_TABLE) {
                    store.table = undefined;
                } else if (store.table.tableLookupCode) {
                    this.preselectedTableLookupCode.set(store.table.tableLookupCode);
                    this.preselectedTableNumber.set(store.table.tableNumber ?? await this.getDefaultTableNumber(store.table.tableLookupCode));
                } else if (this.hasPreselectedTable()) {
                    await this.applyTableToStore(this.preselectedTableLookupCode(), store);
                    this.preselectedTableNumber.set(store.table.tableNumber);
                    tableChanged = true;
                }

                if (!store.orderAheadLaterTimeStore) {
                    this.expectedCompletion.set(undefined);
                }
            }

            if (storeChanged) {
                if (store) {
                    this.authService.setLocationContext(store.locationId, store.outletId, store.storeId);
                } else {
                    this.authService.clearLocationContext();
                }

                this.updateCurrentStoreLocation();

                // TODO: The following will need to be improved to allow non-destructive change of store when possible
                this.orderModel.invalidateOrder();

                void this.sessionModel.notifyAppUsage(false);
            }

            if (storeChanged || tableChanged) {
                this.storeDetailsChanged$.next(storeChanged);
                return this.analyticsService.trackSetStore();
            }
        }
    }

    private async routeToExternalStore(store: StoreData, orderType: OrderType): Promise<boolean> {
        if (store && (store.parentAppStore || (store.externalAppURL && store.externalStoreId))) {
            this._currentStore.set(undefined);
            this.navigationService.redirectToUrl(await this.configurationModel.getURLForExternalStore(store, orderType));
            return true;
        }
        return false;
    }

    public async routeToParentApp(): Promise<void> {
        if (this._parentAppURL()) {
            this._currentStore.set(undefined);
            this.navigationService.redirectToUrl(await this.configurationModel.getURLForParentApp());
        }
    }

    public async applyOrderType(orderType: OrderType): Promise<void> {
        if (this.orderType() === orderType) {
            return;
        }
        this.orderType.set(orderType);

        // If we have a single store location, we can auto-select the store
        // TODO - this will currently invalidate the order if the store changes
        if (orderType && this._singleStoreLocation() && (!this._currentStore() || !this._currentStore().canOrder(orderType))) {
            await this.setCurrentStore(this._singleStoreLocation().getStoreForOrderType(this.orderType()), orderType);
        }

        // If switching to table order, apply the preselected table to the selected store, otherwise remove the table from the selected store.
        if (this._currentStore()) {
            if (orderType === OrderType.ORDER_AT_TABLE && this.hasPreselectedTable()) {
                await this.applyTableToStore(this.preselectedTableLookupCode(), this._currentStore());
            } else if (orderType !== OrderType.ORDER_AT_TABLE) {
                this._currentStore().table = undefined;
            }
        }

        // Remove any deals (and their items) that are not available for this order type
        this.dealModel.removeDeals((deal: DealData) => !deal.isAvailableForOrderType(this.orderType()));

        // Remove order items that are not available for this order type
        void this.orderModel.removeUnavailableOrderItems({orderType: orderType});
    }

    /**
     * Applies the specified table lookup code to the specified store.
     * Looks for a possible custom table number mapping against the store for the table lookup code.
     * @param tableLookupCode
     * @param store
     */
    public async applyTableToStore(tableLookupCode: string, store: StoreData) {
        const storeTable: TableData = store.availableTables.find((table: TableData) => table.tableLookupCode === tableLookupCode);
        store.table.tableLookupCode = tableLookupCode;
        store.table.tableNumber = storeTable?.tableNumber ?? await this.getDefaultTableNumber(tableLookupCode);
    }

    public async getDefaultTableNumber(tableLookupCode: string): Promise<string> {
        tableLookupCode = tableLookupCode.trim();
        if (tableLookupCode.indexOf(" ") == -1 && !this.configurationModel.app()?.appSettings.find((setting: AppSettingData) => setting.name === "disableDefaultTablePrefix" && setting.value === "true")) {
            tableLookupCode = await firstValueFrom(this.translate.get("woa.common.order.atTable.placement.noun.uc")) + " " + tableLookupCode;
        }
        return tableLookupCode.trim();
    }

    public async changeOrderingContext(timeOnly: boolean): Promise<void> {

        // There is only one possible action for time-only mode.
        // This method should not get called if the preconditions are not met, but we check anyway.
        if (timeOnly) {
            return this.canSelectOrderTime() ? this.changeExpectedCompletion() : undefined;
        }

        // If we don't have both an order-type and a store already selected, then we leave store and/or order-type selection
        // up to the ordering-context-selection page. It will prompt for each one as required, and will not require pre-selection
        // of the order type unless necessary (i.e. the order type can be selected from the store list).
        // The ordering-context-selection page also handles store and table selection for waiters.
        if (!this.orderType() || !this._currentStore() || (this.configurationModel.isWaiterActor() && !this._singleStoreLocation())) {
            return this.changeStoreOrOrderType();
        }

        // When neither store nor order type can be changed (usually the case for most venues), we can bypass the ordering context edit page for pickup or table orders.
        // For table orders, we go straight to table selection.
        // For pickup, we go straight to time selection.
        if (this._currentStore() && this._singleOrderType() && !this.canChangeStore()) {
            if (this.orderType() === OrderType.ORDER_AT_TABLE) {
                return this.changeFulfilmentDetails();
            }
            if (this.orderType() === OrderType.ORDER_AHEAD) {
                return this.canSelectOrderTime() ? this.changeExpectedCompletion() : undefined;
            }
        }

        this.orderModel.orderErrorStates().clearOrderingContextErrors();
        await this.untilExpectedCompletionEvaluationComplete();
        return this.navigationService.createPage("woa-ordering-context-edit-page");
    }

    public async changeOrderType(): Promise<void> {
        this.orderModel.orderErrorStates().clearOrderingContextErrors();
        await this.selectAndApplyStoreAndOrderType(new OrderingContext()
            .withIntention(OrderingContextIntention.SELECT_ORDER_TYPE));
    }

    public async changeStoreOrOrderType(orderType?: OrderType): Promise<void> {
        this.orderModel.orderErrorStates().clearOrderingContextErrors();
        await this.untilExpectedCompletionEvaluationComplete();
        await this.selectAndApplyStoreAndOrderType(new OrderingContext()
            .withIntention(OrderingContextIntention.SELECT_STORE)
            .withDefaultOrderType(orderType)
            // For store groups, we don't inform the selection page of the order type. This causes it to ask for store selection first, rather than table number.
            .withSelectedOrderType(orderType ? orderType : this.hasStoreGroup() ? undefined : this.orderType())
            .withSelectedTable(this.preselectedTableSelection()));
    }

    public async changeFulfilmentDetails(): Promise<void> {

        // For a single store-location, store selection cannot change.
        if (this._singleStoreLocation()) {
            void this.selectAndApplyContext(new OrderingContext()
                .withIntention(OrderingContextIntention.CHANGE_FULFILMENT_DETAILS)
                .withDefaultOrderType(this.orderType())
                .withSelectedOrderType(this.orderType()));
        }

        // For multi-store, delivery address or table changes may result in a new store being selected
        else {
            void this.selectAndApplyStoreAndOrderType(new OrderingContext()
                .withIntention(OrderingContextIntention.CHANGE_FULFILMENT_DETAILS)
                .withSelectedStore(this.orderType() === OrderType.ORDER_AT_TABLE ? undefined : this._currentStore())
                .withDefaultOrderType(this.orderType())
                .withSelectedOrderType(this.orderType())
                .withSelectedTable(this.preselectedTableSelection()));
        }
    }

    public async changeExpectedCompletion(): Promise<void> {
        const previousExpectedCompletion: Date = this.expectedCompletion();
        if (!await this.navigationService.createPage("woa-order-time-selection-page", {
            hideAsap: !this.expectedCompletion() && this.orderModel.showOrderTimeAllocationNotice()
        })) {
            return;
        }

        if (this.orderModel.initiatedOrder()) {
            this.orderModel.initiatedOrder().estimatedPickupLocalDateTime = undefined;
            this.orderModel.initiatedOrder().estimatedCompletionLocalDateTime = undefined;
            this.orderModel.initiatedOrder().cannotAllocateOrderLaterDateTime = false;
        }

        return this.loadingService.performWithSpinner(async () => {
            this._evaluatingExpectedCompletionChange.set(true);
            try {
                await this.confirmExpectedCompletionChange(previousExpectedCompletion);
                await this.loadStores(true);
                this._evaluatingExpectedCompletionChange.set(false);
            } catch (error) {
                this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.notification.expectedCompletionCouldNotBeChanged"));
                this._evaluatingExpectedCompletionChange.set(false);
                console.error(error);
            }
        });
    }

    /**
     * Selects a store and/or order type as necessary, and applies them to the ordering context
     * @param orderingContext
     */
    public async selectAndApplyStoreAndOrderType(orderingContext: OrderingContext = new OrderingContext()): Promise<void> {
        if (!this.stores() && !await this.loadStoresForSelection(orderingContext)) {
            return;
        }

        // If there is only one store-location, select or change the order type,
        // and select the store automatically based on the order type.
        let orderType: OrderType = orderingContext.selectedOrderType || orderingContext.defaultOrderType;

        if (!this._singleStoreLocation() && this._currentStore() && orderType && !this.orderType()) {
            void this.applyOrderingContext(orderingContext
                .withSelectedStore(this.getCurrentStoreForOrderType(orderType))
                .withSelectedOrderType(orderType));
            return;
        }

        if (this._singleStoreLocation() && orderType) {
            const newStore: StoreData = this._singleStoreLocation().getStoreForOrderType(orderType);
            if (newStore) {
                void this.applyOrderingContext(orderingContext
                    .withSelectedOrderType(orderType)
                    .withSelectedStore(newStore));
            }
            return;
        }

        if (this._singleStoreLocation() || (this._currentStore() && !orderType && !!orderingContext.selectedMenuItem)) {
            orderingContext = await this.navigationService.createPage("woa-ordering-context-selection-page", {
                orderingContext: orderingContext
                    .withIntention(OrderingContextIntention.SELECT_ORDER_TYPE)
            });
            if (orderingContext) {
                const newStore: StoreData = this._singleStoreLocation()
                    ? this._singleStoreLocation().getStoreForOrderType(orderingContext.selectedOrderType)
                    : this.getCurrentStoreForOrderType(orderingContext.selectedOrderType);
                if (newStore) {
                    void this.applyOrderingContext(orderingContext
                        .withSelectedStore(newStore));
                }
            }
            return;
        }

        // If switching order type to pickup or table order; and the current selected store in the session model supports the new order type;
        // and (for table order) there is a preselected table: change the order type and retain the selected store.
        const orderTypeChange: boolean = orderType && orderType !== this.orderType();
        if (orderTypeChange && (orderType === OrderType.ORDER_AHEAD || (orderType === OrderType.ORDER_AT_TABLE && this.hasPreselectedTable())) &&
            this._currentStore() && this._currentStore().canOrder(orderType)) {
            const newStore: StoreData = <StoreData>EntityData.create(StoreData).assign(this._currentStore());
            newStore.table = undefined;
            void this.applyOrderingContext(orderingContext
                .withSelectedStore(newStore)
                .withSelectedOrderType(orderType));
            return;
        }

        // Warn the user that their order will be cleared if the store changes
        if (this.orderModel.orderHasItems() &&
            this.configurationModel.isConsumerActor() &&
            !(this.orderType() === OrderType.ORDER_AT_TABLE && orderingContext.intention === OrderingContextIntention.CHANGE_FULFILMENT_DETAILS) &&
            !await this.notificationService.displayConfirmation("woa.heading.ordering-context.confirmStoreChange", undefined,
                "woa.message.ordering-context.confirmStoreChange",
                "woa.button.common.noThanks", "woa.button.common.proceed", true)) {
            return;
        }

        return this.selectAndApplyContext(orderType === OrderType.ORDER_AT_TABLE
            ? orderingContext.withSelectedOrderType(orderType)
            : orderingContext.withDefaultOrderType(orderType));
    }

    /**
     * Launches the ordering context selection page and applies the ordering context (if any) returned by that page.
     *
     * @param orderingContext The seed ordering context
     */
    public async selectAndApplyContext(orderingContext: OrderingContext): Promise<void> {
        orderingContext = await this.navigationService.createPage("woa-ordering-context-selection-page", {orderingContext});
        if (orderingContext && await this.applyOrderingContext(orderingContext) && !this.menuId() && !orderingContext.selectedStore) {
            void this.selectAndApplyStoreAndOrderType(orderingContext.withSelectedTable(this.preselectedTableSelection(), false));
        }
    }

    /**
     * Set the current details based on the provided ordering context.
     * @param orderingContext
     */
    public async applyOrderingContext(orderingContext: OrderingContext): Promise<boolean> {
        if (!orderingContext) {
            return true;
        }

        const storeChanged: boolean = orderingContext.selectedStore && (!this._currentStore() || orderingContext.selectedStore.storeId !== this._currentStore().storeId);
        const tableChanged: boolean =
            (orderingContext.selectedStore && this._currentStore() && orderingContext.selectedStore.table.tableLookupCode !== this._currentStore().table.tableLookupCode) ||
            !!orderingContext.selectedTable?.tableLookupCode ||
            (orderingContext.selectedOrderType === OrderType.ORDER_AT_TABLE && !this._currentStore()?.table.tableLookupCode && this.hasPreselectedTable());

        // If the store changes, the current order will be cleared.
        // Give the user the chance to opt-out
        if (storeChanged) {
            if (this.orderModel.orderHasItems() &&
                !await this.notificationService.displayConfirmation("woa.heading.order.confirmRestartOrder", undefined,
                    "woa.message.order.confirmRestartOrder",
                    "woa.button.common.noThanks",
                    "woa.button.order.restartOrder",
                    true)) {
                return false;
            }
            if (await this.routeToExternalStore(orderingContext.selectedStore, orderingContext.selectedOrderType)) {
                return true;
            }
        }

        // No store change or no order to clear
        if (orderingContext.selectedOrderType != undefined) {
            const confirmed: boolean = await this.confirmOrderTypeChange(orderingContext.selectedOrderType);
            if (!confirmed) {
                return false;
            }
        }

        // Apply selected delivery details.
        if (orderingContext.selectedDeliveryDetails) {
            this.applyDeliveryDetails(orderingContext.selectedDeliveryDetails);
        }

        // Apply details of the selected table.
        // These will replace any pre-selections provided via URL parameters, and may lock down certain aspects of the UI.
        const prevExclusiveStoreId: number = this._exclusiveStoreId();
        const prevExclusiveStoreIdListSt: string = this._exclusiveStoreIdList()?.join(",") ?? "";
        const prevTableOnlyMode: boolean = this._tableOnlyMode();
        const prevPickupOnlyMode: boolean = this._pickupOnlyMode();
        const prevDeliveryOnlyMode: boolean = this._deliveryOnlyMode();
        let tableLookupCodeSet: boolean = false;
        let tableNumberSet: boolean = false;
        let storesSet: boolean = false;
        let tableOnlyModeSet: boolean = false;
        let pickupOnlyModeSet: boolean = false;
        let deliveryOnlyModeSet: boolean = false;
        if (orderingContext.selectedTable) {
            if (orderingContext.selectedTable.tableLookupCode) {
                this.preselectedTableLookupCode.set(orderingContext.selectedTable.tableLookupCode);
                tableLookupCodeSet = true;
            }
            if (orderingContext.selectedTable.tableNumber) {
                this.preselectedTableNumber.set(orderingContext.selectedTable.tableNumber);
                tableNumberSet = true;
            }
            if (orderingContext.selectedTable.storeIds || orderingContext.selectedTable.storeGroup) {
                this.preselectedStoreId.set(undefined);
                this._exclusiveStoreId.set(undefined);
                this.preselectedStoreGroup.set(undefined);
                this._exclusiveStoreGroup.set(undefined);
                this.preselectedStoreIdList.set(undefined);
                this._exclusiveStoreIdList.set(undefined);
                if (orderingContext.selectedTable.storeGroup) {
                    this.preselectedStoreGroup.set(orderingContext.selectedTable.storeGroup);
                    this._exclusiveStoreGroup.set(orderingContext.selectedTable.exclusiveStores ? this.preselectedStoreGroup() : undefined);
                } else if (orderingContext.selectedTable.storeIds && orderingContext.selectedTable.storeIds.length > 0) {
                    if (orderingContext.selectedTable.storeIds.length === 1) {
                        this.preselectedStoreId.set(orderingContext.selectedTable.storeIds[0]);
                        this._exclusiveStoreId.set(orderingContext.selectedTable.exclusiveStores ? this.preselectedStoreId() : undefined);
                    } else {
                        this.preselectedStoreIdList.set(orderingContext.selectedTable.storeIds);
                        this._exclusiveStoreIdList.set(orderingContext.selectedTable.exclusiveStores ? this.preselectedStoreIdList() : undefined);
                    }
                }
                storesSet = true;
            }
            if (orderingContext.selectedTable.tableOnlyMode != undefined) {
                this._tableOnlyMode.set(orderingContext.selectedTable.tableOnlyMode);
                tableOnlyModeSet = true;
            }
            if (orderingContext.selectedTable.pickupOnlyMode != undefined) {
                this._pickupOnlyMode.set(orderingContext.selectedTable.pickupOnlyMode);
                pickupOnlyModeSet = true;
            }
            if (orderingContext.selectedTable.deliveryOnlyMode != undefined) {
                this._deliveryOnlyMode.set(orderingContext.selectedTable.deliveryOnlyMode);
                deliveryOnlyModeSet = true;
            }
        }
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        this.applyOrderingContextToParamConfig(orderingContext, paramConfig);
        if (orderingContext.selectedStore && orderingContext.selectedOrderType === OrderType.ORDER_AT_TABLE && orderingContext.selectedStore.table.tableLookupCode) {
            if (!tableLookupCodeSet) {
                this.preselectedTableLookupCode.set(undefined);
            }
            if (!tableNumberSet) {
                this.preselectedTableNumber.set(undefined);
            }
            if (!storesSet) {
                this.preselectedStoreId.set(undefined);
                this._exclusiveStoreId.set(undefined);
                this.preselectedStoreGroup.set(undefined);
                this._exclusiveStoreGroup.set(undefined);
                this.preselectedStoreIdList.set(undefined);
                this._exclusiveStoreIdList.set(undefined);
            }
            if (!tableOnlyModeSet) {
                this._tableOnlyMode.set(paramConfig.tableOnlyMode);
            }
            if (!pickupOnlyModeSet) {
                this._pickupOnlyMode.set(paramConfig.pickupOnlyMode);
            }
            if (!deliveryOnlyModeSet) {
                this._deliveryOnlyMode.set(paramConfig.deliveryOnlyMode);
            }
        }
        if (this._exclusiveStoreId() !== prevExclusiveStoreId ||
            (this._exclusiveStoreIdList() ? this._exclusiveStoreIdList().join(",") : "") !== prevExclusiveStoreIdListSt ||
            this._tableOnlyMode() !== prevTableOnlyMode ||
            this._pickupOnlyMode() !== prevPickupOnlyMode ||
            this._deliveryOnlyMode() !== prevDeliveryOnlyMode) {
            await this.loadStores(true);
        }

        if (storeChanged || tableChanged) {
            if (!storeChanged && tableChanged && orderingContext.selectedOrderType === OrderType.ORDER_AT_TABLE &&
                orderingContext.selectedTable?.tableLookupCode && orderingContext.selectedStore?.table.tableLookupCode !== orderingContext.selectedTable!.tableLookupCode) {
                const tableStore: StoreData = <StoreData>EntityData.create(StoreData).assign(orderingContext.selectedStore);
                tableStore.table.tableLookupCode = orderingContext.selectedTable.tableLookupCode;
                tableStore.table.tableNumber = orderingContext.selectedTable.tableNumber;
                orderingContext = orderingContext.withSelectedStore(tableStore);
            }
            await this.setCurrentStore(orderingContext.selectedStore, orderingContext.selectedOrderType);
        }

        // Changing the order type will remove any order items or deals that are no longer available
        if (orderingContext.selectedOrderType != undefined) {
            await this.applyOrderType(orderingContext.selectedOrderType);
        }

        // The store hasn't changed, but we will reload the schedules, and remove any products that are no longer available
        if (!storeChanged && this.activeSchedulesSearchModel) {
            await this.refreshActiveSchedules("ordering context applied");
        }

        // Initiate ordering of a selected product.
        if (orderingContext.selectedMenuItem) {
            void this.menuModel.orderItemWhenMenuLoaded(orderingContext.selectedMenuItem);
        }

        return true;
    }

    public applyDeliveryDetails(deliveryDetails: DeliveryDetails): void {
        this.mobilePhone.set(deliveryDetails.orderMobilePhone);
        this.deliveryInstructions.set(deliveryDetails.deliveryInstructions);
        this.address.set(deliveryDetails.address);
        this._saveAddressForMember.set(deliveryDetails.saveAddressForMember && this.identityModel.currentMember().allowProfileEdit);

        this.mobilePhoneTrusted.set(true);
        this.deliveryInstructionsTrusted.set(true);
        this.addressTrusted.set(true);
    }

    /**
     * Apply the saved customer contact details to the ordering context.
     *
     * @param force If true, all details are applied whether present in the saved details or not, and whether already trusted or not.
     * This is intended for the case where the user is directly editing their contact details. Details not present are cleared and marked as untrusted,
     * so that they can be defaulted back to member contact details, which supports clearing individual fields.
     * If false, only present details which are not already trusted are applied. This is intended for use when a member
     * has logged in, and we want to retain any trusted details the user has already supplied.
     */
    public applySavedCustomerDetails(force: boolean): CustomerDetails {
        const customerDetails: CustomerDetails = this.customerDetailsService.loadCustomer();

        if (force || (!this.firstNameTrusted() && !this.lastNameTrusted() && (customerDetails.firstName || customerDetails.lastName))) {
            this.firstName.set(customerDetails.firstName);
            this.lastName.set(customerDetails.lastName);
            this.firstNameTrusted.set(!!(customerDetails.firstName || customerDetails.lastName));
            this.lastNameTrusted.set(!!(customerDetails.firstName || customerDetails.lastName));
        }
        if (force || (!this.emailTrusted() && customerDetails.email)) {
            this.email.set(customerDetails.email);
            this.emailTrusted.set(!!customerDetails.email);
        }
        if (force || (!this.mobilePhoneTrusted() && customerDetails.mobilePhone)) {
            this.mobilePhone.set(customerDetails.mobilePhone);
            this.mobilePhoneTrusted.set(!!customerDetails.mobilePhone);
        }

        return customerDetails;
    }

    /**
     * Apply the contact details from the saved customer details, and maybe from the logged-in identity, to the ordering context.
     * Details from the logged-in identity are not "trusted" to be saved for future use on the device,
     * and will not override previously saved or recently entered contact details for use on the order.
     * As such, individual details are only applied if they are flagged as untrusted
     * (i.e. if they are unset or obtained only from a previously logged-in member's details, rather than from the user).
     */
    public applyIdentityContactDetails(currentIdentity: IdentityData, customerDetails?: CustomerDetails): void {

        // Reload the saved customer details if not provided (provided details are assumed to have been saved).
        // We keep any details we have which are already trusted (member login shouldn't cause already entered details to be lost).
        if (!customerDetails) {
            customerDetails = this.applySavedCustomerDetails(false);
        }

        // Apply untrusted identity-details where that's all we have
        if (!this.firstNameTrusted() && !this.lastNameTrusted()) {
            this.firstName.set(currentIdentity.firstName);
            this.lastName.set(currentIdentity.lastName);
        }
        if (!this.emailTrusted()) {
            this.email.set(currentIdentity.email);
        }
        if (!this.mobilePhoneTrusted()) {
            this.mobilePhone.set(currentIdentity.mobilePhone);
        }
        if (!this.deliveryInstructionsTrusted()) {
            this.deliveryInstructions.set(currentIdentity.deliveryInstructions);
        }
        if (!this.addressTrusted()) {
            this.address.set(<AddressData>EntityData.create(AddressData).assign(currentIdentity.homeAddress));
        }

        if (!customerDetails.marketingOptInDecided && this.configurationModel.isConsumerActor()) {
            this._showMarketingOptIn.set(this.configurationModel.app()?.showMarketingOptIn);
            if (this._showMarketingOptIn() && this.marketingOptIn() == undefined) {
                this.marketingOptIn.set(this.configurationModel.app()?.defaultMarketingOptIn);
            }
        }
    }

    /**
     * Ensure the param config matches the ordering context for redirecting to another app
     *
     * @param orderingContext
     * @param paramConfig
     */
    private applyOrderingContextToParamConfig(orderingContext: OrderingContext, paramConfig: ParamConfig): void {
        if (orderingContext.selectedTable) {
            if (orderingContext.selectedTable.tableLookupCode) {
                paramConfig.tableLookupCode = orderingContext.selectedTable.tableLookupCode;
            }
            if (orderingContext.selectedTable.storeGroup) {
                paramConfig.storeGroup = orderingContext.selectedTable.storeGroup;
                paramConfig.exclusiveStoreGroup = orderingContext.selectedTable.exclusiveStores ? this.preselectedStoreGroup() : undefined;
            }
            if (orderingContext.selectedTable.storeIds && orderingContext.selectedTable.storeIds.length > 0) {
                if (orderingContext.selectedTable.storeIds.length === 1) {
                    paramConfig.store = orderingContext.selectedTable.storeIds[0];
                    paramConfig.exclusiveStore = orderingContext.selectedTable.exclusiveStores ? this.preselectedStoreId() : undefined;
                } else {
                    paramConfig.stores = orderingContext.selectedTable.storeIds;
                    paramConfig.exclusiveStores = orderingContext.selectedTable.exclusiveStores ? this.preselectedStoreIdList() : undefined;
                }
            }
            if (!orderingContext.selectedTable.localTable) {
                if (orderingContext.selectedTable.tableOnlyMode != undefined) {
                    paramConfig.tableOnlyMode = orderingContext.selectedTable.tableOnlyMode;
                }
                if (orderingContext.selectedTable.pickupOnlyMode != undefined) {
                    paramConfig.pickupOnlyMode = orderingContext.selectedTable.pickupOnlyMode;
                }
                if (orderingContext.selectedTable.deliveryOnlyMode != undefined) {
                    paramConfig.deliveryOnlyMode = orderingContext.selectedTable.deliveryOnlyMode;
                }
            }
        } else if (orderingContext.selectedStore && orderingContext.selectedStore.table.tableNumber) {
            paramConfig.tableLookupCode = orderingContext.selectedStore.table.tableNumber;
        }
        this.configurationModel.setParamConfig(paramConfig);
    }

    private async applyPreselectedTableToSelectedStore(selectedStore: StoreData): Promise<StoreData> {
        if (this.hasPreselectedTable() && selectedStore.orderAtTableStore && (this.preselectedOrderType() === OrderType.ORDER_AT_TABLE || !(this._pickupOnlyMode() && selectedStore.orderAheadStore))) {
            selectedStore = <StoreData>EntityData.create(StoreData).assign(selectedStore);
            this.orderType.set(OrderType.ORDER_AT_TABLE);
            await this.applyTableToStore(this.preselectedTableLookupCode(), selectedStore);
        } else if (this.preselectedOrderType() && selectedStore.canOrder(this.preselectedOrderType())) {
            this.orderType.set(this.preselectedOrderType());
        }
        return selectedStore;
    }

    private updateCurrentStoreLocation(): void {
        this._currentStoreLocation.set(this._singleStoreLocation() ? this._singleStoreLocation()
            : this._currentStore() ? new StoreLocation(this._currentStore().collectiveStoreName, this._currentStore().locationName, this.stores().filter((store: StoreData) => store.locationName === this._currentStore().locationName))
                : undefined);
    }

    private async confirmOrderTypeChange(orderType: OrderType): Promise<boolean> {
        if (!orderType || this.orderType() === orderType) {
            return true;
        }

        // Check for deals that are not available for this order type
        if (this.dealModel.appliedDeals().some((deal: DealData) => !deal.isAvailableForOrderType(orderType)) &&
            !await this.notificationService.displayConfirmation("woa.heading.order.confirmRemoveUnavailableDeals", undefined,
                orderType === OrderType.ORDER_AHEAD ? "woa.message.order.dealRemoveUnavailableOrderAhead"
                    : orderType === OrderType.ORDER_DELIVERY ? "woa.message.order.dealRemoveUnavailableOrderDelivery"
                        : "woa.message.order.dealRemoveUnavailableOrderAtTable",
                "woa.button.common.cancel",
                "woa.button.common.proceed", true)) {
            return false;
        }

        return this.orderModel.removeUnavailableOrderItems({
            orderType: orderType,
            confirm: true
        });
    }

    private async confirmExpectedCompletionChange(previousExpectedCompletion: Date): Promise<void> {
        let confirmed: boolean = false;
        try {
            confirmed = await this.refreshActiveSchedules("expected completion changed", true);
        } finally {
            if (confirmed) {
                // When changing the order required date/time, we need to reload the menu due to specials
                void this.menuModel.loadMenu("order required date/time changed");
            } else {
                this.expectedCompletion.set(previousExpectedCompletion);
                await this.refreshActiveSchedules("expected completion reverted");
            }
        }
    }

    public async refreshActiveSchedules(reason: string, confirm: boolean = false): Promise<boolean> {
        console.log(Date.now(), "Refreshing active schedules:", reason);

        this.activeSchedulesSearchModel.filter["orderDate"] = this.expectedCompletionAsString();
        await this.activeSchedulesSearchModel.retrieve();
        return this.orderModel.removeUnavailableOrderItems({
            schedules: true,
            confirm: confirm
        });
    }

    public async untilExpectedCompletionEvaluationComplete(): Promise<void> {
        await this.loadingService.performWithSpinner(
            () => whenSignalMatches(this._evaluatingExpectedCompletionChange, (evaluating: boolean) => !evaluating, {injector: this.injector}),
            undefined, "woa.message.order-time.evaluatingTimes");
    }

    /**
     * Returns the current store for an order type
     * @param orderType
     */
    private getCurrentStoreForOrderType(orderType: OrderType): StoreData {
        return this._currentStoreLocation() ? this._currentStoreLocation().getStoreForOrderType(orderType) : this._currentStore();
    }


    public async promptForTip(): Promise<boolean> {
        if (this.configurationModel.isWaiterActor() || this.tipSelected() || !this._currentStore().allowTips || !this._currentStore().highlightTips) {
            return true;
        }
        return this.navigationService.createPage("woa-tip-selection-page");
    }

    public async promptForFirstName(transferTargetName: string): Promise<boolean> {
        if (this.firstName()) {
            return true;
        }
        const name: string = await this.navigationService.createPage("woa-name-edit-page", {
            transferTargetName: transferTargetName
        });
        if (name) {
            this.firstName.set(name);
            this.firstNameTrusted.set(true);
            return true;
        }
        return false;
    }

    public async confirmPhoneNumberForSms(mobilePhone?: string, promptIfMissing: boolean = false, onBeforePrompt?: () => Promise<void>): Promise<boolean> {

        // If a phone number is explicitly provided (e.g. from a wallet payment response), it must be checked against the confirmation list.
        const checkConfirmationList: boolean = !!mobilePhone;

        if (!mobilePhone) {
            mobilePhone = this.mobilePhone();
        }
        if (mobilePhone) {
            mobilePhone = mobilePhone.replace(/\s/g, "");
        }

        // Do nothing for a missing phone number unless a prompt is required (typically only for integrated payments, where a phone number is expected to be returned)
        if (!mobilePhone && !promptIfMissing) {
            return true;
        }

        // Generally, there is no need to confirm a mobile number if it is not required for an order ready notification
        // (order received notifications are not considered important enough to require prompting, and will be silently ignored for an invalid number).
        // However, if any notifications are to be sent, we always request confirmation if the number is on the list of specific numbers requiring confirmation.
        const sendOrderReadySms: boolean = this.configurationModel.app()?.sendOrderReadySms &&
            (this.orderType() !== OrderType.ORDER_AT_TABLE || !this.configurationModel.app()?.sendTblOrderReadyForBistroOnly || this._currentStore().sendTableOrderReady);
        const confirmationRequired: boolean = checkConfirmationList && (sendOrderReadySms || this.configurationModel.app()?.sendOrderReceivedSms) &&
            this.configurationModel.mobileNumberRequiresConfirmation()(mobilePhone);
        if (!confirmationRequired && (!sendOrderReadySms || this.configurationModel.isValidMobileNumberForSms()(mobilePhone))) {
            if (mobilePhone && mobilePhone !== this.mobilePhone()) {
                this.mobilePhone.set(mobilePhone);
                this.mobilePhoneTrusted.set(true);
            }
            return true;
        }

        if (onBeforePrompt) {
            await onBeforePrompt();
        }
        const result: {
            confirmed: boolean,
            mobilePhone?: string
        } = await this.navigationService.createPage("woa-phone-edit-page", {
            mobilePhone: mobilePhone,
            confirmationRequired: confirmationRequired
        });
        if (result && result.confirmed) {
            this.mobilePhone.set(result.mobilePhone);
            this.mobilePhoneTrusted.set(true);
            return true;
        }
        return false;
    }
}
